import {Route, Routes} from 'react-router-dom';
import AssetClassesOverview from './assetClasses/AssetClassesOverview';
import {CreateAssetClass} from './assetClasses/create/CreateAssetClass';
import {AssetClassLanguageVersions} from './assetClasses/edit/AssetClassLanguageVersions';
import {UpdateAssetClass} from './assetClasses/edit/UpdateAssetClass';
import {AssetClassInstruments} from './assetClasses/assetInstruments/AssetClassInstruments';
import {MutualFundInventoryPredictionPage} from './assetClasses/assetInstruments/inventory/MutualFundInventoryPredictionPage';
import {InstrumentShell} from './v2/InstrumentShell';
import BondOverview from './Bonds/BondOverview';
import {BondsLegalDocument} from './Bonds/BondsLegalDocument';
import {BondsInstrumentSettings} from './Bonds/BondsInstrumentSettings';
import {BondsUsersInvite} from './Bonds/BondsUsersInvite';
import {persistStore} from 'redux-persist';
import store from 'redux/store';
import {PersistGate} from 'redux-persist/integration/react';
import {BondsGeneralDetails} from './Bonds/BondsGeneralDetails';
import {BondsTrading} from './Bonds/BondsTrading';
import BondsStepTwoOverview from './Bonds/stepTwo/BondsStepTwoOverview';
import BondsBrokerCompliance from './Bonds/stepTwo/BondsBrokerCompliance';
import RatingsOverview from './ratings/RatingsOverview';
import {AssetInstrumentLanguages} from './assetClasses/assetInstruments/LanguageVersions';
import {GroupInstrumentsForm} from './GroupInstruments/GroupInstrumentsForm';
import {
  MutualFundDetails,
  MutualFundsInvestment,
  MutualFundsOverview,
} from './MutualFunds';
import {IpoDetails} from './Equities/IpoDetails';
import IpoOffering from './Equities/IpoOffering';
import {PublicEquityDetails} from './Equities/PublicEquityDetails';

const persistor = persistStore(store);

export const AssetManagementRoute = () => {
  return (
    <Routes>
      <Route path="/classes" element={<AssetClassesOverview />} />
      <Route path="/classes/create" element={<CreateAssetClass />} />
      <Route path="/classes/ratings" element={<RatingsOverview />} />
      <Route
        path="/:assetClass/:key/create-instrument-group"
        element={<GroupInstrumentsForm />}
      />
      <Route
        path="/:assetClass/:key/update-instrument-group/:id"
        element={<GroupInstrumentsForm />}
      />
      <Route
        path="/classes/update/:language/:id"
        element={<UpdateAssetClass />}
      />
      <Route
        path="/class/:key/instruments"
        element={<AssetClassInstruments />}
      />
      <Route
        path="/class/:key/inventory-prediction"
        element={<MutualFundInventoryPredictionPage />}
      />

      <Route
        path="/class/:key/instruments/:id/languages"
        element={<AssetInstrumentLanguages />}
      />

      <Route
        path="/classes/languages/:id"
        element={<AssetClassLanguageVersions />}
      />
    </Routes>
  );
};

// Step 1 of the instrument shell. The Daba Score, Pitch Deck, FAQ, Analogs,
// Analysis, Risk & Impact, Team, Key Statistics, Investment Contract and
// Investment Details screens were removed in the FinancialInstrument
// migration — the gateway no longer exposes any of the fields they edited.
const baseInstrumentRoute = [
  {path: '/', element: <BondsGeneralDetails />},
  {path: '/trading', element: <BondsTrading />},
  {path: '/overview', element: <BondOverview />},
  {path: '/documents', element: <BondsLegalDocument />},
  {path: '/users-invite', element: <BondsUsersInvite />},
  {path: '/settings', element: <BondsInstrumentSettings />},
];

const bondsRoutes = [
  {path: '/step-two', element: <BondsStepTwoOverview />},
  {path: '/step-two/broker', element: <BondsBrokerCompliance />},
];

const mutualFundsRoutes = [
  {path: '/step-two', element: <MutualFundsOverview />},
  {path: '/step-two/funds', element: <MutualFundDetails />},
  {path: '/step-two/investments', element: <MutualFundsInvestment />},
];

const renderBaseInstrumentRoute = () => {
  return baseInstrumentRoute.map(({path, element}) => (
    <Route key={path} path={path} element={element} />
  ));
};

const renderBondsRoutes = () => {
  return bondsRoutes.map(({path, element}) => (
    <Route key={path} path={path} element={element} />
  ));
};

export const CreateBondRoute = () => {
  return (
    <PersistGate loading={null} persistor={persistor}>
      <Routes>
        <Route path="/" element={<InstrumentShell type="bond" />}>
          {renderBaseInstrumentRoute()}
          {renderBondsRoutes()}
        </Route>
      </Routes>
    </PersistGate>
  );
};

export const UpdateBondRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<InstrumentShell type="bond" />}>
        {renderBaseInstrumentRoute()}
        {renderBondsRoutes()}
      </Route>
    </Routes>
  );
};

const renderMutualFundRoutes = () => {
  return mutualFundsRoutes.map(({path, element}) => (
    <Route key={path} path={path} element={element} />
  ));
};

const ipoRoutes = [
  {path: '/step-two', element: <IpoDetails />},
  {path: '/step-two/profile', element: <IpoOffering />},
];
const equityRoutes = [{path: '/step-two', element: <PublicEquityDetails />}];

const renderRoutes = routes =>
  routes.map(({path, element}) => (
    <Route key={path} path={path} element={element} />
  ));

export const CreateIpoRoute = () => (
  <Routes>
    <Route path="/" element={<InstrumentShell type="ipo" />}>
      {renderBaseInstrumentRoute()}
      {renderRoutes(ipoRoutes)}
    </Route>
  </Routes>
);

export const CreatePublicEquityRoute = () => (
  <Routes>
    <Route path="/" element={<InstrumentShell type="public-equity" />}>
      {renderBaseInstrumentRoute()}
      {renderRoutes(equityRoutes)}
    </Route>
  </Routes>
);

export const CreateMutualFundRoute = () => {
  return (
    <Routes>
      <Route path="/" element={<InstrumentShell type="mutual-funds" />}>
        {renderBaseInstrumentRoute()}
        {renderMutualFundRoutes()}
      </Route>
    </Routes>
  );
};
